/*
 * Sends newsletter signups straight to a Klaviyo list instead of creating a
 * Shopify customer. Applies to any form carrying data-klaviyo-list and
 * data-klaviyo-company; without both the browser performs the normal Shopify
 * submit, so an unconfigured list never breaks the signup. The same happens
 * when a tracker blocker cuts off the request to Klaviyo.
 */
(function () {
  if (window.klaviyoNewsletterLoaded) return;
  window.klaviyoNewsletterLoaded = true;

  const ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
  // The API version. Older ones reject parts of the payload below.
  const REVISION = '2026-07-15';

  // The signup a form is currently waiting for; answers to older ones are dropped.
  const pending = new WeakMap();

  function findMessage(form, type) {
    return form.querySelector('[data-klaviyo-message="' + type + '"]');
  }

  function resetMessages(form) {
    ['error', 'success'].forEach(function (type) {
      const element = findMessage(form, type);
      if (element) element.hidden = true;
    });
  }

  function showMessage(form, type, text) {
    const element = findMessage(form, type);
    if (!element) return;

    if (text) {
      const textNode = element.querySelector('[data-klaviyo-message-text]');
      if (textNode) textNode.textContent = text;
    }

    // Never an error and a thank-you next to each other.
    resetMessages(form);
    // No focus() here: the messages announce themselves via role, and a focus
    // ring around a line of text looks like a bug.
    element.hidden = false;
  }

  function setBusy(form, busy) {
    const button = form.querySelector('[type="submit"]');
    if (!button) return;

    button.toggleAttribute('disabled', busy);
    button.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  /*
   * The endpoint subscribes the address to the list on its own, so the profile
   * carries nothing but the address. Whether a confirmation mail is sent, and
   * thus when the profile appears in the list, follows the opt-in setting of
   * the list in Klaviyo.
   */
  function payload(email, listId, source) {
    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: source,
          profile: {
            data: {
              type: 'profile',
              attributes: { email: email },
            },
          },
        },
        relationships: { list: { data: { type: 'list', id: listId } } },
      },
    };
  }

  async function onSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const listId = form.dataset.klaviyoList;
    const companyId = form.dataset.klaviyoCompany;
    if (!listId || !companyId) return;

    event.preventDefault();
    resetMessages(form);

    /*
     * Clicking the button and pressing Enter can start two signups at once.
     * Only the answer to the last one is shown, otherwise the error of the one
     * and the thank-you of the other end up on screen together.
     */
    const run = (pending.get(form) || 0) + 1;
    pending.set(form, run);

    const input = form.querySelector('input[type="email"]');
    const email = input ? input.value.trim() : '';
    if (!email || (input && !input.checkValidity())) {
      showMessage(form, 'error', form.dataset.klaviyoInvalidEmail);
      if (input) input.focus();
      return;
    }

    setBusy(form, true);

    try {
      const response = await fetch(ENDPOINT + '?company_id=' + encodeURIComponent(companyId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          revision: REVISION,
        },
        body: JSON.stringify(payload(email, listId, form.dataset.klaviyoSource || 'Website')),
      });

      if (!response.ok) throw new Error('Klaviyo responded with ' + response.status);
      if (pending.get(form) !== run) return;

      if (input) input.value = '';
      showMessage(form, 'success');
    } catch (error) {
      console.error('[klaviyo-newsletter]', error);
      if (pending.get(form) !== run) return;

      /*
       * a.klaviyo.com is on the tracker lists, so Firefox in strict mode and
       * every ad blocker cut the request off. fetch then fails with a
       * TypeError, and instead of losing the address the normal Shopify signup
       * takes over. form.submit() does not run this handler again.
       */
      if (error instanceof TypeError) {
        form.submit();
        return;
      }

      showMessage(form, 'error', form.dataset.klaviyoError);
    } finally {
      if (pending.get(form) === run) setBusy(form, false);
    }
  }

  document.addEventListener('submit', onSubmit);
})();
